/**
 * Accordion.tsx — Renders a collapsible accordion using MUI components.
 *
 * JSON trigger: `uiCompType.method: "renderAccordion"`
 * Data field:   `sectionData.items` — Array of `SectionData` objects where
 *               each item has a `title` (summary row) and `description` / `text`
 *               (expanded body content).
 *
 * **Behaviour:**
 *  - Uses MUI `Accordion` (controlled by `expanded` state).
 *  - Summary row: renders `item.title` as a `Typography` node.
 *  - Detail body: renders `item.description` via `transformDescription` or
 *    `item.text` as plain `Typography`.
 *  - Only one accordion panel is open at a time (standard MUI behaviour).
 *  - Icons: `AddIcon` when collapsed, `RemoveIcon` when expanded.
 *
 * **Style slots:** `uiCompType.styles` (outer wrapper).
 */
import React from "react";
import { Typography } from "@mui/material";
import { Accordion as MuiAccordion, AccordionDetails, AccordionSummary } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import cssStyles from "./cms_renderer.module.scss";
import { transformDescription } from "./shared";
import { SectionData, ComponentRenderProps, renderSection } from "./index";
import Title from "./Title";

interface AccordionProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

// Accordion component
const Accordion = ({ sectionData, props }: AccordionProps): JSX.Element | null => {
    //console.log("Accordion...");
    const panelClassName = "accordion";

    const [expanded, setExpanded] = React.useState<string | false>(false);

    const handleChange =
        (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    return (
        <div className={cssStyles[panelClassName]} style={{ ...sectionData.uiCompType?.styles, ...(sectionData.uiCompType?.containerStyles || {}) }}>
            {sectionData.title && <Title sectionData={sectionData} props={props} />}
            {sectionData.description && <Typography variant="body1" className={cssStyles[panelClassName + "Description"]}>
                {transformDescription(sectionData.description, "", sectionData.trackId, undefined, undefined, props)}
            </Typography>}
            <div className={cssStyles[panelClassName + "Container"]}>
                {sectionData.items && (sectionData.items as SectionData[]).map((item: SectionData, index: number) => (
                    <MuiAccordion key={item.trackId ?? `${index}-${item.title ?? sectionData.title ?? "item"}`} expanded={expanded === `panel${index}`}
                        onChange={handleChange(`panel${index}`)}
                        sx={{ boxShadow: 'none', '&:before': { 'opacity': '1 !important' }, '&.Mui-expanded': { margin: '0px !important' } }}
                        className={cssStyles[panelClassName + "AccordionItem"]}>
                        <AccordionSummary
                            expandIcon={expanded === `panel${index}` ? <RemoveIcon /> : <AddIcon />}
                            aria-controls={`panel${index}bh-content`}
                            id={`panel${index}bh-header`}
                            className={cssStyles["AccordionSummary"]}
                            sx={{
                                minHeight: 'initial !important',
                                '.Mui-expanded': {
                                    margin: '10px 0 !important'
                                }
                            }}
                        >
                            <strong>{item.title}</strong>
                        </AccordionSummary>
                        <AccordionDetails className={cssStyles["AccordionDetails"]}>
                            {transformDescription(item.description, undefined, item.trackId, undefined, undefined, props)}
                        </AccordionDetails>
                    </MuiAccordion>
                ))}
            </div>
        </div>
    );
}
export default Accordion;